/* Copyright (c) 2020-present CloudIO Inc */

import {
  useStateXAction,
  useStateXActionCaller,
  useStateXAddToUndo,
  useStateXAtomValue,
  useStateXGetter,
} from '@cloudio-saas/statex';
import { IconButton, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { ThemeColor } from 'componentregistry';
import zIndex from 'constants/ZIndex';
import {
  activeItemIdAtom,
  activeItemIdsAtom,
  designPath,
  itemAtom,
  pageAtom,
} from 'designer/atoms';
import { canDuplicate } from 'designer/ContextMenu';
import {
  deleteAction,
  duplicateAction,
  moveBottomAction,
  moveDownAction,
  moveTopAction,
  moveUpAction,
} from 'designer/state';
import Icon from 'icons/Icon';
import { usePlatform } from 'platform/PlatformHooks';
import React, { useEffect, useState } from 'react';
import { isViewOnlyDeveloper } from 'root/useUser';

export const useDeveloperToolbarStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: `0 2px 4px ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    right: 20,
    padding: '4px 4px',
    position: 'absolute',
    top: 65,
    zIndex: zIndex.DeveloperToolbar,
    display: 'flex',
  },
}));

export const useToolStyles = makeStyles((theme: Theme) => ({
  active: {
    '& svg': {
      fill: theme.palette.secondary.main,
    },
  },
  activeRed: {
    '& svg': {
      fill: theme.palette.error.main,
    },
  },
  disabled: {
    cursor: 'wait',
    '& svg': {
      fill: theme.palette.text.disabled,
    },
  },
  button: {
    padding: 0,
    width: 32,
    height: 32,
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      margin: 0,
    },
  },
}));

interface ToolProp {
  onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  label: string;
  icon: string;
  disabled?: boolean;
  styleName?: 'activeRed' | 'active';
  color?: ThemeColor;
}

export function Tool({
  onClick,
  styleName,
  label,
  icon,
  disabled,
  color,
}: ToolProp): React.ReactElement {
  const classes = useToolStyles();
  const className = clsx(
    {
      [classes.button]: true,
      [classes.disabled]: disabled,
    },
    styleName ? classes[styleName] : null,
  );
  return (
    <IconButton
      className={className}
      size="small"
      data-tip={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      <span aria-hidden="false" style={{ display: 'none' }}>
        {label}
      </span>
      <Icon className={classes.icon} iconName={icon} color={color} />
    </IconButton>
  );
}

export default function SelectedItemToolbar({
  appUid,
  pageId,
}: {
  appUid: string;
  pageId: string;
}): React.ReactElement | null {
  const get = useStateXGetter();
  const classes = useDeveloperToolbarStyles();
  const addToUndo = useStateXAddToUndo('io');
  const duplicate = useStateXAction(duplicateAction);
  const itemId = useStateXAtomValue(activeItemIdAtom, {
    params: { appUid, pageId, treename: 'designer' },
  });
  const ids = useStateXAtomValue(activeItemIdsAtom, {
    params: { appUid, pageId },
  });
  const multipleItemSelected = ids.length > 1;
  const itemValue = useStateXAtomValue(itemAtom, {
    params: { appUid, pageId, treename: 'designer', itemId: itemId || '_x_' },
  });
  useStateXAtomValue(itemAtom, {
    params: {
      appUid,
      pageId,
      treename: 'designer',
      itemId: itemValue?.parent || '_x_',
    },
  });
  const deleteItemFn = useStateXAction(deleteAction);
  const [deletedItemId, setDeletedItemId] = useState('');
  const platform = usePlatform();
  const call = useStateXActionCaller();

  useEffect(() => {
    setDeletedItemId('');
  }, [itemId]);

  if (itemId === 'root') {
    return null;
  }

  const page = get(pageAtom, { params: { appUid, pageId } });
  if (!page) {
    return null;
  }
  const item = page[itemId];

  if (!item) {
    return null;
  }

  const disabled = !item || itemId === deletedItemId;
  const dup = item ? canDuplicate(platform, appUid, pageId, item, get) : false;
  const parent = page[item.parent];
  const isViewOnlyUser = isViewOnlyDeveloper(platform, appUid);

  return (
    <div className={classes.root}>
      <Tool
        disabled={disabled || !dup || multipleItemSelected || isViewOnlyUser}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) {
            addToUndo(designPath({ appUid, pageId }), pageId);
            duplicate({ appUid, pageId, itemId, platform });
          }
        }}
        label="Duplicate"
        icon="clone"
      />
      <Tool
        disabled={
          multipleItemSelected ||
          parent.type === 'Page' ||
          parent.children.indexOf(itemId) === 0 ||
          isViewOnlyUser
        }
        onClick={(e) => {
          e.stopPropagation();
          addToUndo(designPath({ appUid, pageId }), pageId);
          call(moveTopAction, { appUid, pageId, itemId });
        }}
        label="Move to Top"
        icon="arrow-to-top"
      />
      <Tool
        disabled={
          multipleItemSelected ||
          parent.type === 'Page' ||
          parent.children.indexOf(itemId) === 0 ||
          isViewOnlyUser
        }
        onClick={(e) => {
          e.stopPropagation();
          addToUndo(designPath({ appUid, pageId }), pageId);
          call(moveUpAction, { appUid, pageId, itemId });
        }}
        label="Move Up"
        icon="arrow-up"
      />
      <Tool
        disabled={
          multipleItemSelected ||
          parent.type === 'Page' ||
          parent.children.indexOf(itemId) === parent.children.length - 1 ||
          isViewOnlyUser
        }
        onClick={(e) => {
          e.stopPropagation();
          addToUndo(designPath({ appUid, pageId }), pageId);
          call(moveDownAction, { appUid, pageId, itemId });
        }}
        label="Move Down"
        icon="arrow-down"
      />
      <Tool
        disabled={
          multipleItemSelected ||
          parent.type === 'Page' ||
          parent.children.indexOf(itemId) === parent.children.length - 1 ||
          isViewOnlyUser
        }
        onClick={(e) => {
          e.stopPropagation();
          addToUndo(designPath({ appUid, pageId }), pageId);
          call(moveBottomAction, { appUid, pageId, itemId });
        }}
        label="Move to Bottom"
        icon="arrow-to-bottom"
      />
      <Tool
        disabled={disabled || isViewOnlyUser}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) {
            setDeletedItemId(itemId);
            addToUndo(designPath({ appUid, pageId }), pageId);
            if (ids.length) {
              ids.forEach((id) => {
                deleteItemFn({ appUid, pageId, itemId: id, platform });
              });
            } else {
              deleteItemFn({ appUid, pageId, itemId, platform });
            }
          }
        }}
        label={
          ids.length
            ? `Delete ${ids.length} selected items`
            : 'Delete selected item'
        }
        icon="trash"
        color="error"
      />
    </div>
  );
}
